import { FetchPost } from '../utils/dataModel';

/**
 * @file AuthService.js
 * @brief Service for handling authentication and authorization
 * @module services/AuthService
 */

/**
 * @class AuthService
 * @brief Service class for managing user authentication
 * 
 * @details
 * Handles all authentication-related operations including login,
 * logout, session management, and token handling. Provides methods
 * for secure communication with the authentication API endpoints.
 */
class AuthService {
  async hashPassword(password) {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * @brief Attempts to authenticate a user
   * @async
   * 
   * @param {string} email - User's email address
   * @param {string} password - User's password
   * @returns {Promise<Object>} Authentication result with token and user info
   * @throws {Error} If authentication fails
   */
  async login(email, password) {
    try {
      const post_data = {
        email: email,
        password: await this.hashPassword(password),
      };
      const response = await FetchPost("/api/auth/login", {}, post_data);
      if (!response.session_id) {
        throw new Error("Session ID not found in response");
      }
      localStorage.setItem("email", email);
      localStorage.setItem("session_id", response.session_id);
      return email;
    } catch (error) {
      console.log(`Error during login: ${error}`);
      const error_items = await error.response.json();
      console.log(JSON.stringify(error_items));

      if (error.status === 400) {
        throw new Error("Wrong email");
      }
      else if (error.status === 401) {
        throw new Error("Wrong password");
      }
      else {
        throw new Error("An error occurred during login");
      }
    }
  }

  /**
   * @brief Logs out the current user
   * @async
   * 
   * @returns {Promise<void>}
   * @throws {Error} If logout fails
   */
  logout() {
    localStorage.removeItem("email");
    localStorage.removeItem("session_id");
  }

  logoutWhenError(error) {
    if (!error) {
      return;
    }
    if ("status" in error && error.status === 401) {
      this.logout();
    }
  }

  /**
   * @brief Initiates password reset process
   * @async
   * 
   * @param {string} email - Email address for password reset
   * @returns {Promise<Object>} Result of password reset request
   * @throws {Error} If password reset request fails
   */
  async forgotPassword(email) {
    try {
      const response = await FetchPost("/api/auth/password/forget", {}, { email });
      return response;
    } catch (error) {
      if (error.status === 400) {
        throw new Error("Invalid email");
      } else {
        throw new Error("An error occurred while processing your request");
      }
    }
  }

  async resetPassword(resetToken, newPassword) {
    try {
      const response = await FetchPost("/api/auth/password/reset", {}, {
        reset_token: resetToken,
        password: await this.hashPassword(newPassword)
      });
      return response;
    } catch (error) {
      if (error.status === 400) {
        throw new Error("Invalid or expired reset token");
      } else {
        throw new Error("An error occurred while resetting your password");
      }
    }
  }

  /**
   * @brief Checks if the current session is valid
   * @async
   * 
   * @returns {Promise<boolean>} True if session is valid
   */
  async validateSession() {
    // ...existing code...
  }

  getCurrentEmail() {
    return localStorage.getItem("email");
  }
}

const auth_service = new AuthService();

export default auth_service;